// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState, useCallback } from 'react'

/**
 * Sidebar layout state.
 *
 * Three modes, decided primarily by viewport width:
 *
 *   desktop  (≥1024px) — sidebar always visible; can be collapsed to icons
 *   tablet   (640–1023) — sidebar collapsed by default; open=true overlays
 *   mobile   (<640px)   — sidebar hidden; open=true is a full overlay
 *
 * Persistence: only the desktop "collapsed" preference persists.
 * Mobile/tablet open state resets to false on each page load — matches
 * how every product handles this.
 */

export type SidebarMode = 'desktop' | 'tablet' | 'mobile'

const COLLAPSED_KEY = 'owlrx.sidebar.collapsed'

function detectMode(): SidebarMode {
  if (typeof window === 'undefined') return 'desktop'
  const w = window.innerWidth
  if (w < 640)  return 'mobile'
  if (w < 1024) return 'tablet'
  return 'desktop'
}

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === '1'
  } catch { return false }
}

function writeCollapsed(value: boolean) {
  try {
    window.localStorage.setItem(COLLAPSED_KEY, value ? '1' : '0')
  } catch { /* ignore */ }
}

export interface SidebarState {
  mode: SidebarMode
  /** Desktop-only: true when sidebar is icon-only. */
  collapsed: boolean
  /** Tablet/mobile-only: true when overlay sidebar is visible. */
  overlayOpen: boolean
  /** Toggle behavior depends on mode. */
  toggle: () => void
  /** Force-close overlay (used by Escape, backdrop click, route change). */
  closeOverlay: () => void
}

export function useSidebarState(): SidebarState {
  const [mode, setMode] = useState<SidebarMode>(detectMode)
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed)
  const [overlayOpen, setOverlayOpen] = useState<boolean>(false)

  // Resize listener
  useEffect(() => {
    function onResize() {
      const next = detectMode()
      setMode((prev: string) => {
        if (prev !== next) {
          // Reset overlay when transitioning between modes
          setOverlayOpen(false)
        }
        return next
      })
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Persist desktop collapse preference
  useEffect(() => { writeCollapsed(collapsed) }, [collapsed])

  // Escape closes overlay
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && overlayOpen) setOverlayOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [overlayOpen])

  const toggle = useCallback(() => {
    if (mode === 'desktop') {
      setCollapsed((c: boolean) => !c)
    } else {
      setOverlayOpen((o: boolean) => !o)
    }
  }, [mode])

  const closeOverlay = useCallback(() => setOverlayOpen(false), [])

  return { mode, collapsed, overlayOpen, toggle, closeOverlay }
}
